import type { OrgWithdrawalAction } from '@safetyhub/shared';
import api, { ApiResponse, extractApiError } from './api';

// 프로필 수정 요청
export interface UpdateProfileRequest {
  name?: string;
  email?: string | null;  // null = 등록 해제 (''는 서버 DTO @IsEmail에 걸린다)
  emailVerificationToken?: string;  // 이메일 등록·변경 시 필수
  // 연락처 이메일(member.email) — 결재·알림 수신 주소. 계정 이메일과 별개 축이라
  // 계정 유형과 무관하게 편집한다. null = 해제(무인증), 값 변경 = 인증 토큰 필수.
  // (결정 기록 2026-08-23-contact-email-redefine-and-source-axis-removal)
  contactEmail?: string | null;
  contactEmailVerificationToken?: string;  // 연락처 이메일 등록·변경 시 필수
  phone?: string;
  phoneVerificationToken?: string;  // 전화번호 변경 시 필수
  rank?: string; // 직급 (사원/대리/과장 등). Phase F-1
  companyId?: string; // rank 저장 대상 회사 스코프 (멀티회사 멤버)
}

// 비밀번호 변경 요청
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// 2단계 탈퇴 요청
export type DownloadMethod = 'direct' | 'sms' | 'email' | 'discard';

// 조직별 처리 방식.
// 어휘는 @safetyhub/shared의 OrgWithdrawalAction 하나뿐이다 — 여기서 따로 선언하면
// 서버와 표기가 갈라진다(그 사고 경위는 shared의 org-withdrawal-action.ts 주석 참조).
//
// ⚠️ 본인 탈퇴에서 보낼 수 있는 값은 **close뿐**이다(결정 기록 2026-08-20 정책 10).
// 소유권 이전은 탈퇴와 분리해 조직 설정(/members)에서 먼저 끝낸다. enum에 TRANSFER가
// 남아 있는 건 admin 강제 탈퇴가 쓰기 때문이고, 여기서 보내면 서버가 400으로 거부한다.
export interface OrgActionConfig {
  action: OrgWithdrawalAction;
}

export interface WithdrawalRequestData {
  password: string;
  orgActions?: Record<string, OrgActionConfig>;  // { [orgId]: { action: 'close' } }
  downloadMethod: DownloadMethod;
  // ⚠️ 수신처(phone/email)는 없다 — 서버가 계정에서 파생하고, body에 실으면
  // `forbidNonWhitelisted`가 400으로 거부한다 (S27 수신자 축, 2026-08-22).
}

export interface WithdrawalRequestResponse {
  exportId: string | null;
  status: 'GENERATING' | null;
  canProceedImmediately: boolean;
  requiresDiscardConfirmation: boolean;
}

export interface WithdrawalConfirmData {
  exportId?: string;
  discardConfirmed?: boolean;
  orgActions?: Record<string, OrgActionConfig>;
}

export interface WithdrawalConfirmResponse {
  message: string;
  recoveryDeadline: string;
}

// 조직 멤버 정보 — 폐쇄 시 영향받는 멤버 수 표시에 쓴다
export interface OrgMember {
  memberId: string;
  userId: string;
  name: string;
  /** 소유자 성질(배정 축, P7-b-2 — 옛 `role` 문자열 대체) */
  isOwner: boolean;
}

// 데이터 내보내기 대상
export interface ExportTargetsResponse {
  /** 개인/팀 작업공간을 **실제로** 소유하고 있는가 (예전엔 서버가 true를 하드코딩했다) */
  personalWorkspace: boolean;
  /**
   * 내보낼 자료가 하나라도 있는가 — false면 탈퇴 1단계(내보내기 방식 선택)를 건너뛰고
   * 최종 확인만 받는다. 서버의 `createExport` 진입 조건과 같은 술어에서 나온다.
   */
  canExport: boolean;
  /**
   * 본인이 유일한 OWNER인 **모든** 조직 — 탈퇴하면 폐쇄된다.
   * 조직 유형(category) 축은 없다: 개인/팀 작업공간도 여기 온다.
   */
  soleOwnerOrganizations: {
    id: string;
    name: string;
    /** 개인/팀 작업공간인가 — 문구를 가르는 축(멤버 없는 작업공간엔 '멤버 접근 차단'이 거짓) */
    isPersonalWorkspace: boolean;
    otherMembers: OrgMember[];
  }[];
}

// 데이터 내보내기 상태
export interface ExportStatusResponse {
  id: string;
  status: 'GENERATING' | 'READY' | 'DOWNLOADED' | 'FAILED' | 'EXPIRED' | 'DELETED';
  downloadMethod: string;
  inspectionCount: number;
  reportCount: number;
  tbmCount: number;
  photoCount: number;
  downloadCount: number;
  maxDownloads: number;
  expiresAt: string;
  canDownload: boolean;
}

// Users API
export const usersApi = {
  // 프로필 수정
  updateProfile: async (data: UpdateProfileRequest): Promise<void> => {
    const response = await api.patch<ApiResponse<unknown>>('/users/me', data);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '프로필 수정에 실패했습니다.');
    }
  },

  // 전화번호 해제 알림 확인 (배너 닫기)
  dismissPhoneRemovedAlert: async (): Promise<void> => {
    const response = await api.post<ApiResponse<unknown>>('/users/me/dismiss-phone-removed-alert');
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '알림 확인에 실패했습니다.');
    }
  },

  // 비밀번호 변경
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    const response = await api.post<ApiResponse<unknown>>(
      '/users/me/change-password',
      data
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || '비밀번호 변경에 실패했습니다.');
    }
  },

  // 탈퇴 요청 (1단계)
  withdrawalRequest: async (data: WithdrawalRequestData): Promise<WithdrawalRequestResponse> => {
    const response = await api.post<ApiResponse<WithdrawalRequestResponse>>(
      '/users/me/withdrawal/request',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '탈퇴 요청에 실패했습니다.');
    }
    return response.data.data;
  },

  // 탈퇴 확인 (2단계)
  withdrawalConfirm: async (data: WithdrawalConfirmData): Promise<WithdrawalConfirmResponse> => {
    const response = await api.post<ApiResponse<WithdrawalConfirmResponse>>(
      '/users/me/withdrawal/confirm',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '탈퇴 확인에 실패했습니다.');
    }
    return response.data.data;
  },
};

// 데이터 내보내기 API
export const dataExportApi = {
  // 내보내기 대상 조회
  getExportTargets: async (): Promise<ExportTargetsResponse> => {
    const response = await api.get<ApiResponse<ExportTargetsResponse>>('/data-export/targets');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '내보내기 대상 조회에 실패했습니다.');
    }
    return response.data.data;
  },

  // 내보내기 상태 조회
  getExportStatus: async (exportId: string): Promise<ExportStatusResponse> => {
    const response = await api.get<ApiResponse<ExportStatusResponse>>(`/data-export/${exportId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || '내보내기 상태 조회에 실패했습니다.');
    }
    return response.data.data;
  },

  // 내보내기 파일 다운로드 (인증된 요청)
  downloadExport: async (exportId: string): Promise<void> => {
    const response = await api.get(`/data-export/${exportId}/download`, {
      responseType: 'blob',
    });

    // Blob에서 파일 다운로드
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `safetyhub_export_${exportId}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export { extractApiError };
